import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Plus,
  X,
  Receipt,
  User,
  Target,
  FileText,
  DollarSign,
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const EditInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError } = useToast();

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'draft',
    client: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    project: '',
    description: '',
    items: [],
    subtotal: 0,
    taxRate: 0,
    taxAmount: 0,
    total: 0,
    notes: '',
    terms: 'Payment due within 30 days of invoice date.',
    paymentMethods: ['Bank Transfer', 'Check', 'Credit Card'],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const location = useLocation();

  useEffect(() => {
    // If invoice was provided via navigation state, populate form immediately
    if (location?.state?.invoice) {
      const src = location.state.invoice || {};
      const formattedItems = (src.items || []).map((item, index) => ({
        id: item.id || `${Date.now()}-init-${index}`,
        description: item.description || '',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        amount: item.amount || 0,
      }));

      setFormData((prev) => ({
        ...prev,
        invoiceNumber: src.invoiceNumber || prev.invoiceNumber || '',
        date: src.date
          ? new Date(src.date).toISOString().split('T')[0]
          : prev.date,
        dueDate: src.dueDate
          ? new Date(src.dueDate).toISOString().split('T')[0]
          : prev.dueDate,
        status: src.status || prev.status,
        clientName: src.clientName || src.counterparty || prev.clientName,
        clientEmail: src.clientEmail || src.email || prev.clientEmail,
        clientPhone: src.clientPhone || src.phone || prev.clientPhone,
        clientAddress: src.clientAddress || src.address || prev.clientAddress,
        project: src.project || prev.project,
        description: src.description || prev.description,
        items: formattedItems.length
          ? formattedItems
          : [
              {
                id: 1,
                description: '',
                quantity: 1,
                rate: 75,
                amount: 75,
              },
            ],
        subtotal: src.subtotal || src.amount || prev.subtotal,
        taxRate: src.taxRate || prev.taxRate,
        taxAmount: src.taxAmount || prev.taxAmount,
        total: src.total || src.amount || prev.total,
        notes: src.notes || src.memo || prev.notes,
        terms: src.terms || prev.terms,
        paymentMethods: src.paymentMethods || prev.paymentMethods,
      }));
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const [invoiceResponse, projectsResponse, customersResponse] =
          await Promise.all([
            fetch(`${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}/${id}`, {
              headers: API_CONFIG.HEADERS,
            }),
            fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
              headers: API_CONFIG.HEADERS,
            }),
            fetch(API_CONFIG.ENDPOINTS.CUSTOMERS, {
              headers: API_CONFIG.HEADERS,
            }),
          ]);

        if (!invoiceResponse.ok) {
          const errorData = await invoiceResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load invoice');
        }

        const invoiceData = await invoiceResponse.json();
        const invoice = invoiceData.data || {};

        if (projectsResponse.ok) {
          const data = await projectsResponse.json();
          const rawProjects =
            data?.data?.projects || data?.projects || data?.data || [];
          setProjects(Array.isArray(rawProjects) ? rawProjects : []);
        }

        if (customersResponse.ok) {
          const data = await customersResponse.json();
          const customers = data.data || [];
          const mappedClients = customers.map((customer) => ({
            id: customer._id || customer.id,
            name: customer.name,
            email: customer.primaryContact?.email || '',
            phone: customer.primaryContact?.phone || '',
            address:
              customer.addresses?.billing?.street ||
              customer.addresses?.headquarters?.street ||
              '',
          }));
          setClients(mappedClients);
        }

        const formattedItems = (invoice.items || []).map((item, index) => ({
          id: item.id || `${Date.now()}-${index}`,
          description: item.description || '',
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          amount: item.amount || 0,
        }));

        setFormData({
          invoiceNumber: invoice.invoiceNumber || '',
          date: invoice.date
            ? new Date(invoice.date).toISOString().split('T')[0]
            : '',
          dueDate: invoice.dueDate
            ? new Date(invoice.dueDate).toISOString().split('T')[0]
            : '',
          status: invoice.status || 'draft',
          client: '',
          clientName: invoice.clientName || '',
          clientEmail: invoice.clientEmail || '',
          clientPhone: invoice.clientPhone || '',
          clientAddress: invoice.clientAddress || '',
          project: invoice.project || '',
          description: invoice.description || '',
          items: formattedItems.length
            ? formattedItems
            : [
                {
                  id: 1,
                  description: '',
                  quantity: 1,
                  rate: 75,
                  amount: 75,
                },
              ],
          subtotal: invoice.subtotal || 0,
          taxRate: invoice.taxRate || 0,
          taxAmount: invoice.taxAmount || 0,
          total: invoice.total || 0,
          notes: invoice.notes || '',
          terms: invoice.terms || 'Payment due within 30 days of invoice date.',
          paymentMethods: invoice.paymentMethods || [
            'Bank Transfer',
            'Check',
            'Credit Card',
          ],
        });
      } catch (err) {
        showError(err.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    );
    const taxAmount = subtotal * (formData.taxRate / 100);
    const total = subtotal + taxAmount;

    setFormData((prev) => ({
      ...prev,
      subtotal,
      taxAmount,
      total,
    }));
  }, [formData.items, formData.taxRate]);

  useEffect(() => {
    if (clients.length > 0) {
      // If client id isn't set, try matching by clientName or email
      if (!formData.client && formData.clientName) {
        const match = clients.find(
          (client) =>
            client.name === formData.clientName ||
            client.email === formData.clientEmail,
        );
        if (match) {
          setFormData((prev) => ({
            ...prev,
            client: match.id,
          }));
        }
      } else if (
        formData.client &&
        !clients.find((c) => c.id === formData.client)
      ) {
        // If formData.client contains a name/string, resolve to id
        const match = clients.find(
          (client) =>
            client.name === formData.client ||
            client.email === formData.client ||
            client.id === formData.client,
        );
        if (match) {
          setFormData((prev) => ({ ...prev, client: match.id }));
        }
      }
    }
  }, [clients, formData.client, formData.clientName]);

  // Ensure project dropdown is preselected when projects load
  useEffect(() => {
    if (projects.length > 0 && formData.project) {
      const match = projects.find(
        (p) =>
          (p._id || p.id) === formData.project || p.name === formData.project,
      );
      if (match && formData.project !== (match._id || match.id)) {
        setFormData((prev) => ({ ...prev, project: match._id || match.id }));
      }
    }
  }, [projects, formData.project]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClientChange = (clientId) => {
    const selectedClient = clients.find((c) => c.id === clientId);
    if (selectedClient) {
      setFormData((prev) => ({
        ...prev,
        client: clientId,
        clientName: selectedClient.name,
        clientEmail: selectedClient.email,
        clientPhone: selectedClient.phone,
        clientAddress: selectedClient.address,
      }));
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      }),
    }));
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 75,
      amount: 75,
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const handleRemoveItem = (itemId) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== itemId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const selectedClient = clients.find((c) => c.id === formData.client);
      const selectedProject = projects.find(
        (p) => (p._id || p.id) === formData.project,
      );

      const payload = {
        ...formData,
        clientName: selectedClient?.name || formData.clientName,
        clientEmail: selectedClient?.email || formData.clientEmail,
        clientPhone: selectedClient?.phone || formData.clientPhone,
        clientAddress: selectedClient?.address || formData.clientAddress,
        project:
          selectedProject?._id || selectedProject?.id || formData.project,
        projectName: selectedProject?.name || formData.projectName || '',
      };

      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}/${id}`,
        {
          method: 'PUT',
          headers: API_CONFIG.HEADERS,
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update invoice');
      }

      success('Invoice updated successfully!');
      navigate('/projects/time-tracking/billing');
    } catch (err) {
      showError(err.message || 'Failed to update invoice. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader
          title="Edit Invoice"
          description="Loading invoice details..."
        />
        <div className="page-container">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '400px' }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Edit Invoice"
        description="Update invoice details and billing information"
      />

      {/* Tab Styles */}
      <style>
        {`
          .invoice-tabs-container {
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 16px;
          }

          .invoice-tabs {
            display: flex;
            gap: 6px;
            padding: 0 8px;
          }

          .invoice-tab {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            font-size: 13px;
            font-weight: 500;
            color: #6b7280;
            background: transparent;
            border: none;
            border-bottom: 2px solid transparent;
            border-radius: 0;
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .invoice-tab.active {
            color: #1e40af;
            border-bottom-color: #3b82f6;
          }

          .invoice-tab:hover:not(.active) {
            color: #374151;
          }

          .invoice-tab svg {
            width: 16px;
            height: 16px;
            flex-shrink: 0;
          }

          .invoice-tab-content {
            padding-top: 16px;
          }

          .invoice-tab-panel {
            display: none;
          }

          .invoice-tab-panel.active {
            display: block;
          }
          
          .section-header {
            margin-bottom: 15px;
          }

          .section-title {
            font-size: 17px;
            font-weight: 600;
            line-height: 1.3;
            margin-bottom: 4px;
          }

          .section-description {
            font-size: 12px;
            color: #6b7280;
          }

          .invoice-tabs-container {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          .project-form-card {
            padding-top: 12px;
          }

          .invoice-tabs {
            margin-bottom: 12px;
            padding-bottom: 4px;
          }

          .invoice-tab-content {
            padding-top: 20px;
          }
          /* Make edit page layout match the stacked view page: hide tabs and show all panels */
          .invoice-tabs { display: none; }
          .invoice-tab-panel { display: block !important; padding-top: 0 !important; }

          /* Inputs styled similarly to view-field for a consistent read/edit appearance */
          .form-control, .form-select, textarea.form-control {
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 8px 12px;
            min-height: 38px;
            font-family: inherit;
            font-size: 14px;
            color: #111827;
          }
          textarea.form-control { align-items: flex-start; }
        `}
      </style>

      <div className="page-container">
        <div className="project-create-container">
          <form onSubmit={handleSubmit}>
            {/* Form Card with Tabs */}
            <div className="project-form-card">
              {/* Tab Navigation */}
              <div className="invoice-tabs-container">
                <div className="invoice-tabs">
                  <button
                    type="button"
                    className={`invoice-tab ${activeTab === 0 ? 'active' : ''}`}
                    onClick={() => setActiveTab(0)}
                  >
                    <Receipt size={16} />
                    Invoice Info
                  </button>
                  <button
                    type="button"
                    className={`invoice-tab ${activeTab === 1 ? 'active' : ''}`}
                    onClick={() => setActiveTab(1)}
                  >
                    <User size={16} />
                    Client
                  </button>
                  <button
                    type="button"
                    className={`invoice-tab ${activeTab === 2 ? 'active' : ''}`}
                    onClick={() => setActiveTab(2)}
                  >
                    <Target size={16} />
                    Project
                  </button>
                  <button
                    type="button"
                    className={`invoice-tab ${activeTab === 3 ? 'active' : ''}`}
                    onClick={() => setActiveTab(3)}
                  >
                    <FileText size={16} />
                    Items
                  </button>
                  <button
                    type="button"
                    className={`invoice-tab ${activeTab === 4 ? 'active' : ''}`}
                    onClick={() => setActiveTab(4)}
                  >
                    <DollarSign size={16} />
                    Summary
                  </button>
                  <button
                    type="button"
                    className={`invoice-tab ${activeTab === 5 ? 'active' : ''}`}
                    onClick={() => setActiveTab(5)}
                  >
                    <FileText size={16} />
                    Additional
                  </button>
                </div>

                {/* Tab Content */}
                <div className="invoice-tab-content">
                  {/* Tab 1: Invoice Information */}
                  <div
                    className={`invoice-tab-panel ${activeTab === 0 ? 'active' : ''}`}
                  >
                    <div className="project-form-section">
                      <div className="section-header">
                        <h3 className="section-title">Invoice Information</h3>
                        <p className="section-description">
                          Basic information about the invoice
                        </p>
                      </div>

                      <div className="form-grid-4">
                        <div className="form-group">
                          <label className="form-label">Invoice Number *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.invoiceNumber}
                            onChange={(e) =>
                              handleInputChange('invoiceNumber', e.target.value)
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Invoice Date *</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formData.date}
                            onChange={(e) =>
                              handleInputChange('date', e.target.value)
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Due Date *</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formData.dueDate}
                            onChange={(e) =>
                              handleInputChange('dueDate', e.target.value)
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={formData.status}
                            onChange={(e) =>
                              handleInputChange('status', e.target.value)
                            }
                          >
                            <option value="draft">Draft</option>
                            <option value="pending">Pending</option>
                            <option value="sent">Sent</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tab 2: Client Information */}
                  <div
                    className={`invoice-tab-panel ${activeTab === 1 ? 'active' : ''}`}
                  >
                    <div className="project-form-section">
                      <div className="section-header">
                        <h3 className="section-title">Client Information</h3>
                        <p className="section-description">
                          Details about the client and billing address
                        </p>
                      </div>

                      <div className="form-grid-4">
                        <div className="form-group">
                          <label className="form-label">Client *</label>
                          <select
                            className="form-select"
                            value={formData.client}
                            onChange={(e) => handleClientChange(e.target.value)}
                            required
                          >
                            <option value="">Select Client</option>
                            {clients.map((client) => (
                              <option key={client.id} value={client.id}>
                                {client.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Client Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.clientName}
                            onChange={(e) =>
                              handleInputChange('clientName', e.target.value)
                            }
                            readOnly
                            style={{ backgroundColor: '#f8f9fa' }}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            value={formData.clientEmail}
                            onChange={(e) =>
                              handleInputChange('clientEmail', e.target.value)
                            }
                            readOnly
                            style={{ backgroundColor: '#f8f9fa' }}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Phone</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.clientPhone}
                            onChange={(e) =>
                              handleInputChange('clientPhone', e.target.value)
                            }
                            readOnly
                            style={{ backgroundColor: '#f8f9fa' }}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Client Address</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.clientAddress}
                          onChange={(e) =>
                            handleInputChange('clientAddress', e.target.value)
                          }
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tab 3: Project Information */}
                  <div
                    className={`invoice-tab-panel ${activeTab === 2 ? 'active' : ''}`}
                  >
                    <div className="project-form-section">
                      <div className="section-header">
                        <h3 className="section-title">Project Information</h3>
                        <p className="section-description">
                          Project details and description
                        </p>
                      </div>

                      <div className="form-grid-4">
                        <div className="form-group">
                          <label className="form-label">Project *</label>
                          <select
                            className="form-select"
                            value={formData.project}
                            onChange={(e) =>
                              handleInputChange('project', e.target.value)
                            }
                            required
                          >
                            <option value="">Select Project</option>
                            {Array.isArray(projects) &&
                              projects.map((project) => (
                                <option
                                  key={project._id || project.id}
                                  value={project._id || project.id}
                                >
                                  {project.name}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.description}
                            onChange={(e) =>
                              handleInputChange('description', e.target.value)
                            }
                            placeholder="Brief description of the work performed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tab 4: Invoice Items */}
                  <div
                    className={`invoice-tab-panel ${activeTab === 3 ? 'active' : ''}`}
                  >
                    <div className="project-form-section">
                      <div className="section-header">
                        <h3 className="section-title">Invoice Items</h3>
                        <p className="section-description">
                          Line items for the invoice
                        </p>
                      </div>

                      <div className="invoice-items">
                        {formData.items.map((item, index) => (
                          <div key={item.id} className="invoice-item-row">
                            <div className="row">
                              <div className="col-md-5">
                                <div className="form-group">
                                  <label className="form-label">
                                    Description *
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={item.description}
                                    onChange={(e) =>
                                      handleItemChange(
                                        item.id,
                                        'description',
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Service description"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-group">
                                  <label className="form-label">Quantity</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={item.quantity}
                                    onChange={(e) =>
                                      handleItemChange(
                                        item.id,
                                        'quantity',
                                        parseFloat(e.target.value),
                                      )
                                    }
                                    min="0"
                                    step="0.25"
                                  />
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-group">
                                  <label className="form-label">Rate ($)</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={item.rate}
                                    onChange={(e) =>
                                      handleItemChange(
                                        item.id,
                                        'rate',
                                        parseFloat(e.target.value),
                                      )
                                    }
                                    min="0"
                                    step="0.01"
                                  />
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-group">
                                  <label className="form-label">
                                    Amount ($)
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={item.amount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </div>

                              <div className="col-md-1">
                                <div className="form-group">
                                  <label className="form-label">&nbsp;</label>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleRemoveItem(item.id)}
                                    disabled={formData.items.length === 1}
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        <div className="d-flex justify-content-start mt-3">
                          <button
                            type="button"
                            className="btn btn-outline d-flex align-items-center"
                            onClick={handleAddItem}
                          >
                            <Plus size={16} />
                            Add Item
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tab 5: Financial Summary */}
                  <div
                    className={`invoice-tab-panel ${activeTab === 4 ? 'active' : ''}`}
                  >
                    <div className="project-form-section">
                      <div className="section-header">
                        <h3 className="section-title">Financial Summary</h3>
                        <p className="section-description">
                          Invoice totals and tax information
                        </p>
                      </div>

                      <div className="form-grid-4">
                        <div className="form-group">
                          <label className="form-label">Subtotal ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.subtotal.toFixed(2)}
                            readOnly
                            style={{ backgroundColor: '#f8f9fa' }}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Tax Rate (%)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.taxRate}
                            onChange={(e) =>
                              handleInputChange(
                                'taxRate',
                                parseFloat(e.target.value),
                              )
                            }
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Tax Amount ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.taxAmount.toFixed(2)}
                            readOnly
                            style={{ backgroundColor: '#f8f9fa' }}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Total ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.total.toFixed(2)}
                            readOnly
                            style={{
                              backgroundColor: '#f8f9fa',
                              fontWeight: '600',
                              fontSize: '16px',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tab 6: Additional Information */}
                  <div
                    className={`invoice-tab-panel ${activeTab === 5 ? 'active' : ''}`}
                  >
                    <div className="project-form-section">
                      <div className="section-header">
                        <h3 className="section-title">
                          Additional Information
                        </h3>
                        <p className="section-description">
                          Notes, terms, and payment information
                        </p>
                      </div>

                      <div className="form-grid-2">
                        <div className="form-group">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={formData.notes}
                            onChange={(e) =>
                              handleInputChange('notes', e.target.value)
                            }
                            placeholder="Additional notes or comments..."
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Payment Terms</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={formData.terms}
                            onChange={(e) =>
                              handleInputChange('terms', e.target.value)
                            }
                            placeholder="Payment terms and conditions..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="project-form-actions">
              <button
                type="button"
                className="btn-outline-cam"
                onClick={() => navigate('/projects/time-tracking/billing')}
              >
                Cancel
              </button>
              <button type="submit" className="btn-cam" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditInvoice;
